import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {
  dataAccessRequestService,
  DataAccessRequest,
  DataAccessRequestType,
} from '../services/dataAccessRequestService';

// FEAT-078 — User-facing MFA-gated data access requests (MVP-18.46).

export default function DataAccessRequestPage() {
  const [requests, setRequests] = useState<DataAccessRequest[]>([]);
  const [requestType, setRequestType] = useState<DataAccessRequestType>('EXPORT');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await dataAccessRequestService.listMine();
      setRequests(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRequests();
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await dataAccessRequestService.submit(requestType);
      setSuccess('Your request was queued. Our team will process it shortly.');
      await loadRequests();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit request';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', py: 4, px: 2 }} data-testid="data-access-request-page">
      <Typography variant="h4" gutterBottom>
        Data Access Requests
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Request an export or deletion of your personal data. Multi-factor authentication must be
        enabled on your account before submitting (MVP-18.46).
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="data-access-error">
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} data-testid="data-access-success">
          {success}
        </Alert>
      ) : null}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="request-type-label">Request type</InputLabel>
              <Select
                labelId="request-type-label"
                label="Request type"
                value={requestType}
                onChange={(e) => setRequestType(e.target.value as DataAccessRequestType)}
                inputProps={{ 'data-testid': 'request-type-select' }}
              >
                <MenuItem value="EXPORT">Export my data</MenuItem>
                <MenuItem value="DELETION">Delete my data</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="info">
              Sign out and sign back in with MFA enabled if you see an MFA error.
            </Alert>
            <Button
              variant="contained"
              onClick={() => void handleSubmit()}
              disabled={submitting}
              data-testid="submit-data-access-request"
              sx={{ '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}
            >
              {submitting ? 'Submitting…' : 'Submit request'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="h6" gutterBottom>
        Your requests
      </Typography>
      {loading ? (
        <CircularProgress size={24} />
      ) : requests.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No requests yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {requests.map((request) => (
            <Card key={request.id} variant="outlined" data-testid={`my-request-${request.id}`}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">{request.requestType}</Typography>
                  <Chip label={request.status} size="small" />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Submitted {new Date(request.createdAt).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
